/**
 * PIEZAS COMUNES
 *
 * Los tres estados que toda pantalla que pide datos tiene que saber
 * pintar: cargando, error y vacío. Están aquí para que ninguna se los
 * invente por su cuenta y para que las tres se vean igual en toda la
 * aplicación.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent as EventoTeclado, type ReactNode } from 'react';

import { Link } from 'react-router-dom';

import { FalloApi } from '../api.ts';
import { estadoLineaOt, estadoNc, estadoOtLimpieza, estadoPlanLimpieza, type DescEstado, type Tono } from '../estados.ts';

export function Cargando({ texto = 'Cargando…' }: { texto?: string }): ReactNode {
  return (
    <div className="cargando" role="status">
      <span className="girador" aria-hidden="true" />
      {texto}
    </div>
  );
}

export function Error({ error, reintentar }: { error: unknown; reintentar?: () => void }): ReactNode {
  const fallo = error instanceof FalloApi ? error : null;
  const mensaje =
    fallo?.message ?? (error instanceof globalThis.Error ? error.message : 'Ha ocurrido un error.');
  return (
    <div className="nota error" role="alert">
      <div>{mensaje}</div>
      {/*  F11.1: la referencia sólo sirve para cruzar un fallo del servidor con su log. Un rechazo
           de negocio o de validación (4xx) ya se explica con su mensaje: ahí sobraba. */}
      {fallo?.requestId !== undefined && fallo.estado >= 500 && (
        <div className="pequena tenue mono" style={{ marginTop: 4 }}>
          ref. {fallo.requestId}
        </div>
      )}
      {reintentar !== undefined && (
        <button className="pequeno" style={{ marginTop: 8 }} onClick={reintentar}>
          Reintentar
        </button>
      )}
    </div>
  );
}

/**
 * IDENTIDAD VISUAL DE UNA ENTIDAD (cliente, y por extensión lo que cuelgue
 * de él): logo si existe, iniciales sobre un color estable en caso
 * contrario. El logo es complementario, nunca sustituye al nombre —
 * siempre se pinta al lado, nunca solo.
 *
 * Ningún cliente tiene `logoUrl` todavía (el modelo no lo tiene: ver
 * propuesta en CONTINUIDAD_FASE_3_1.md). El prop existe para que, el día
 * que la API lo sirva, sólo haga falta pasarlo — ningún sitio que use
 * `Avatar` tiene que cambiar.
 */
export function Avatar({
  nombre,
  logoUrl,
  pequeno = false,
}: {
  nombre: string;
  logoUrl?: string | null;
  pequeno?: boolean;
}): ReactNode {
  const tam = pequeno ? 22 : 32;
  if (logoUrl !== undefined && logoUrl !== null && logoUrl !== '') {
    return (
      <img
        src={logoUrl}
        alt=""
        className="avatar avatar-img"
        style={{ width: tam, height: tam }}
      />
    );
  }
  return (
    <span
      className="avatar avatar-iniciales"
      style={{ width: tam, height: tam, fontSize: pequeno ? 10 : 12, background: colorDe(nombre) }}
      aria-hidden="true"
    >
      {inicialesDe(nombre)}
    </span>
  );
}

function inicialesDe(nombre: string): string {
  const palabras = nombre.trim().split(/\s+/).filter(Boolean);
  if (palabras.length === 0) return '?';
  const primera = palabras[0]?.[0] ?? '';
  const segunda = palabras.length > 1 ? (palabras[palabras.length - 1]?.[0] ?? '') : '';
  return (primera + segunda).toUpperCase();
}

//  Paleta fija y estable: el mismo nombre siempre cae en el mismo color,
//  para que un cliente sea reconocible de un vistazo entre varios.
const PALETA_AVATAR = ['#0f6ea8', '#5a6b7b', '#1a7f5a', '#9a6008', '#7a4fa0', '#a5292b', '#0d7f8f'];

function colorDe(texto: string): string {
  let h = 0;
  for (let i = 0; i < texto.length; i++) h = (h * 31 + texto.charCodeAt(i)) >>> 0;
  return PALETA_AVATAR[h % PALETA_AVATAR.length] ?? PALETA_AVATAR[0]!;
}

/**
 * UX-1C — EmptyState: vacío sobrio (sin ilustraciones). `motivo` distingue
 * el VACÍO REAL («todavía no hay…») del vacío POR FILTROS («no hay
 * resultados con los filtros actuales»), que además ofrece limpiarlos.
 */
export function EmptyState({ titulo, descripcion, accion, icono, motivo = 'vacio', children }: {
  titulo: string;
  descripcion?: ReactNode;
  accion?: ReactNode;
  icono?: ReactNode;
  motivo?: 'vacio' | 'filtros';
  children?: ReactNode;
}): ReactNode {
  return (
    <div className={`estado-caja estado-vacio${motivo === 'filtros' ? ' por-filtros' : ''}`} data-motivo={motivo}>
      {icono !== undefined && <div className="estado-vacio-icono" aria-hidden="true">{icono}</div>}
      <h3>{titulo}</h3>
      {descripcion !== undefined && <p className="estado-vacio-descripcion">{descripcion}</p>}
      {children}
      {accion !== undefined && <div className="estado-vacio-accion">{accion}</div>}
    </div>
  );
}

/** Vacío por filtros: mismo texto en toda la aplicación y, si se puede, un «Limpiar filtros». */
export function SinResultados({ limpiar }: { limpiar?: () => void }): ReactNode {
  return (
    <EmptyState motivo="filtros" titulo="No hay resultados con los filtros actuales."
      descripcion="Prueba a cambiar o quitar algún filtro."
      accion={limpiar === undefined ? undefined : <button type="button" onClick={limpiar}>Limpiar filtros</button>} />
  );
}

/** Compatibilidad: el vacío de siempre es un EmptyState sin descripción. */
export function Vacio({ titulo, children }: { titulo: string; children?: ReactNode }): ReactNode {
  return <EmptyState titulo={titulo}>{children}</EmptyState>;
}

/**
 * Carga de datos con sus tres estados.
 *
 * Sin librería de datos: `useEffect` + `AbortController` implícito por la
 * bandera `vivo`. Es lo justo para este vertical; el día que haga falta
 * caché o revalidación se cambia aquí y no en veinte pantallas.
 */
export function useDatos<T>(
  cargar: () => Promise<T>,
  dependencias: readonly unknown[],
): { datos: T | null; cargando: boolean; error: unknown; recargar: () => void } {
  const [datos, setDatos] = useState<T | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [ronda, setRonda] = useState(0);

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    setError(null);
    cargar()
      .then((d) => {
        if (vivo) setDatos(d);
      })
      .catch((e: unknown) => {
        if (vivo) setError(e);
      })
      .finally(() => {
        if (vivo) setCargando(false);
      });
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencias, ronda]);

  return { datos, cargando, error, recargar: () => setRonda((r) => r + 1) };
}

/**
 * ¿El ancho actual cae en el corte móvil? (OT-UX-DEBT-01, DEBT-ZERO-01)
 *
 * `anchoMaximo` por defecto 600px: el mismo corte que ya decide en CSS
 * cuál de las dos representaciones —tabla o tarjetas— se ve en cada
 * pantalla (`.tabla-ots-desktop`/`.lista-ots-movil`, `estilos.css`). Esto
 * no decide NINGÚN estilo —eso sigue siendo sólo CSS—; sólo permite que
 * una pantalla elija, en JS, un DEFAULT distinto (orden por ejemplo) según
 * cuál de las dos representaciones es la que de verdad se ve.
 *
 * `resize`, no `matchMedia`: jsdom no trae `matchMedia` y añadir un shim
 * sólo para esto sería más código que la propia comprobación. `innerWidth`
 * sí lo trae (con un valor por defecto que cae del lado "escritorio"), así
 * que las pruebas de componente no necesitan preparar nada especial.
 */
export function useEsMovil(anchoMaximo = 600): boolean {
  const [movil, setMovil] = useState(() => window.innerWidth <= anchoMaximo);
  useEffect(() => {
    function alRedimensionar(): void {
      setMovil(window.innerWidth <= anchoMaximo);
    }
    window.addEventListener('resize', alRedimensionar);
    return () => window.removeEventListener('resize', alRedimensionar);
  }, [anchoMaximo]);
  return movil;
}

/** Envoltorio para no repetir el mismo `if (cargando) … if (error) …`. */
export function Panel<T>({
  estado,
  children,
  vacio,
}: {
  estado: { datos: T | null; cargando: boolean; error: unknown; recargar: () => void };
  children: (datos: T) => ReactNode;
  vacio?: ReactNode;
}): ReactNode {
  if (estado.cargando && estado.datos === null) return <Cargando />;
  if (estado.error !== null) return <Error error={estado.error} reintentar={estado.recargar} />;
  if (estado.datos === null) return vacio ?? <Vacio titulo="Sin datos" />;
  return <>{children(estado.datos)}</>;
}

/**
 * MODAL OPERATIVO (FASE 4.3-F)
 *
 * Sustituye al panel que se renderizaba al final del listado de `/ots`
 * para la confirmación/verificación multi-OT — la UAT encontró que
 * obligaba a desplazarse hasta el final de una lista larga para ver lo
 * que se acababa de pedir. Ancho en escritorio, prácticamente a pantalla
 * completa en móvil (ver `.modal` en `estilos.css`); cabecera fija,
 * cuerpo con scroll interno propio, pie fijo para las acciones finales
 * — nunca hay que desplazarse fuera del modal para confirmar o para ver
 * cuántas líneas quedan.
 *
 * Cierra con Escape o con un clic en el fondo — nunca pierde nada que
 * importe: lo que hay dentro es un formulario efímero (checkboxes,
 * fechas) que ya se podía descartar con el botón «Cerrar» que esto
 * sustituye.
 */
export function Modal({
  titulo,
  cerrar,
  children,
  pie,
}: {
  titulo: ReactNode;
  cerrar: () => void;
  children: ReactNode;
  pie?: ReactNode;
}): ReactNode {
  const idTitulo = useId();
  const dialogo = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function alTeclear(e: KeyboardEvent): void {
      if (e.key === 'Escape') cerrar();
    }
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [cerrar]);

  //  UX-1A — foco accesible sin librería: al abrir, al primer campo del cuerpo (o al propio diálogo); Tab y
  //  Mayús+Tab no salen del diálogo; al cerrar, el foco vuelve a quien lo abrió (si sigue en la página).
  useEffect(() => {
    const previo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const d = dialogo.current;
    if (d !== null && !d.contains(document.activeElement)) {
      const campo = d.querySelector<HTMLElement>('.modal-cuerpo input:not([type="hidden"]):not(:disabled), .modal-cuerpo select:not(:disabled), .modal-cuerpo textarea:not(:disabled)');
      (campo ?? d).focus();
    }
    return () => {
      if (previo !== null && previo.isConnected) previo.focus();
    };
  }, []);

  function atraparFoco(e: EventoTeclado<HTMLDivElement>): void {
    if (e.key !== 'Tab' || dialogo.current === null) return;
    const enfocables = [...dialogo.current.querySelectorAll<HTMLElement>(
      'a[href], button:not(:disabled), input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
    )];
    if (enfocables.length === 0) return;
    const primero = enfocables[0]!;
    const ultimo = enfocables[enfocables.length - 1]!;
    if (e.shiftKey && (document.activeElement === primero || document.activeElement === dialogo.current)) { e.preventDefault(); ultimo.focus(); }
    else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
  }

  return (
    <div
      className="modal-fondo"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) cerrar();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby={idTitulo} ref={dialogo} tabIndex={-1} onKeyDown={atraparFoco}>
        <div className="modal-cabecera">
          <h2 id={idTitulo}>{titulo}</h2>
          <button type="button" className="pequeno" onClick={cerrar} aria-label="Cerrar">
            Cerrar
          </button>
        </div>
        <div className="modal-cuerpo">{children}</div>
        {pie !== undefined && <div className="modal-pie">{pie}</div>}
      </div>
    </div>
  );
}

/**
 * ESTÁNDAR VISUAL DE ESTADOS (FASE 4.3, BUG 4)
 *
 * Fuente ÚNICA de verdad para "código de estado → etiqueta legible" y
 * "código de estado → variante de color", para OT, línea de OT y NC. Antes
 * de esto, `EstadoOt`/`EstadoLinea`/`EstadoNc` pintaban el CÓDIGO crudo
 * (con guiones bajos) como texto — sólo el color salía de un mapa — y ese
 * mapa ni siquiera cubría el dominio completo de FASE4 (`VERIFICADA`,
 * `CERRADA` y `REABIERTA` de OT cadan al gris por defecto). Nunca se tocan
 * los códigos de backend aquí: sólo su presentación.
 *
 * Un código sin entrada explícita cae a `{ etiqueta: estado, variante:
 * 'neutra' }` (mismo estado crudo, mismo gris de antes) — nunca revienta
 * por un valor legado o futuro que este mapa todavía no conozca.
 */
interface DescripcionEstado {
  readonly etiqueta: string;
  readonly variante: string;
}

/**
 * UX-1C — StatusBadge: sólo presentación. Recibe `label` y `tone` ya
 * decididos (por `estados.ts` o por la página); no conoce ningún enum ni
 * regla de negocio. El marcador de forma (::before, decorativo) añade una
 * segunda señal además del color; el TEXTO es siempre la señal principal.
 */
export function StatusBadge({ label, tone, size = 'md', icon, className = '' }: {
  label: string;
  tone: Tono;
  size?: 'sm' | 'md';
  icon?: ReactNode;
  className?: string;
}): ReactNode {
  return (
    <span className={`etiqueta estado tono-${tone}${size === 'sm' ? ' estado-sm' : ''}${className === '' ? '' : ` ${className}`}`} data-tono={tone}>
      {icon !== undefined && <span className="estado-icono" aria-hidden="true">{icon}</span>}
      {label}
    </span>
  );
}

/** Atajo: pinta un `DescEstado` del mapa central. */
export function Estado({ d, size }: { d: DescEstado; size?: 'sm' | 'md' }): ReactNode {
  return <StatusBadge label={d.label} tone={d.tone} size={size} />;
}

/**
 * UX-1C — DOMINIO, nunca estado: marcador corto («OT» Limpieza, «PT»
 * Mantenimiento…) con borde propio y sin color de estado.
 */
const DOMINIOS = {
  LIMPIEZA: { corto: 'OT', nombre: 'Limpieza' },
  MANTENIMIENTO: { corto: 'PT', nombre: 'Mantenimiento' },
  INVENTARIO: { corto: 'INV', nombre: 'Inventario' },
  CALIDAD: { corto: 'NC', nombre: 'Calidad' },
} as const;
export type Dominio = keyof typeof DOMINIOS;
export function MarcaDominio({ dominio }: { dominio: Dominio }): ReactNode {
  const d = DOMINIOS[dominio];
  return <abbr className={`marca-dominio dominio-${dominio.toLowerCase()}`} title={d.nombre} aria-label={d.nombre}>{d.corto}</abbr>;
}

/**
 * UX-1C — Leyenda discreta de estados (planners). En móvil se pliega
 * (`<details>`): nunca ocupa más que una línea hasta que se abre.
 */
export function LeyendaEstados({ titulo = 'Estados', estados, extra }: { titulo?: string; estados: readonly DescEstado[]; extra?: ReactNode }): ReactNode {
  return (
    <details className="leyenda-estados" open={typeof window === 'undefined' || window.innerWidth > 600}>
      <summary>{titulo}</summary>
      <ul aria-label={`Leyenda: ${titulo}`}>
        {estados.map((d) => <li key={d.label}><StatusBadge label={d.label} tone={d.tone} size="sm" /></li>)}
        {extra}
      </ul>
    </details>
  );
}

function descripcionDe(
  mapa: Readonly<Record<string, DescripcionEstado>>,
  estado: string,
): DescripcionEstado {
  return mapa[estado] ?? { etiqueta: estado, variante: 'neutra' };
}

/** Estado de OT de limpieza — etiqueta y tono del mapa central (`estados.ts`, UX-1C). */
export function EstadoOt({ estado, size }: { estado: string; size?: 'sm' | 'md' }): ReactNode {
  return <Estado d={estadoOtLimpieza(estado)} size={size} />;
}

/** Sólo la etiqueta, para sitios que no admiten un `ReactNode` (p.ej. `<option>`). */
export function etiquetaEstadoOt(estado: string): string {
  return estadoOtLimpieza(estado).label;
}

/** Estado de una línea de OT (FASE 4.2-A/4.3-A). NO_APLICA es neutro; NO_OK, fallo. */
export function EstadoLinea({ estado }: { estado: string }): ReactNode {
  return <Estado d={estadoLineaOt(estado)} />;
}

/** Sólo la etiqueta, para resúmenes de texto (p.ej. "2 no elegibles: 1 verificada, 1 no OK"). */
export function etiquetaEstadoLinea(estado: string): string {
  return estadoLineaOt(estado).label;
}

/**
 * UX-POLISH-01 (punto 5): resumen legible de un conjunto de líneas NO
 * elegibles para una acción masiva, agrupado por estado — "1 verificada,
 * 1 no OK" — nunca la lista línea a línea (saturaría el modal) ni el
 * código crudo del estado. Usado por los modales multi-OT de confirmar y
 * verificar para no dejar que una línea excluida desaparezca en silencio.
 */
export function resumenLineasPorEstado(lineas: readonly { readonly estado: string }[]): string {
  const conteo = new Map<string, number>();
  for (const l of lineas) conteo.set(l.estado, (conteo.get(l.estado) ?? 0) + 1);
  return [...conteo.entries()].map(([estado, n]) => `${String(n)} ${etiquetaEstadoLinea(estado).toLowerCase()}`).join(', ');
}

/**
 * UX-POLISH-01 (puntos 4 y 5): resumen de selección para los modales
 * multi-OT (confirmar/verificar). Antes, el título del modal ("4 OT
 * seleccionada(s)") y este párrafo ("2 OT · 5 líneas...") podían mostrar
 * dos cifras de OT distintas sin ninguna relación explícita entre
 * ellas — confuso cuando parte de la selección no tenía líneas
 * elegibles. Ahora, si las dos cifras coinciden se muestra la frase
 * corta de siempre; si no coinciden, se explica la diferencia
 * ("De ellas, N tienen líneas..."). Las líneas no elegibles nunca
 * desaparecen en silencio: se cuentan y, agrupadas por estado, se dice
 * por qué quedaron fuera.
 */
export function ResumenSeleccionMultiOt({
  otsSeleccionadas,
  otsAfectadas,
  lineasElegibles,
  lineasNoElegibles,
  etiquetaAccion,
}: {
  otsSeleccionadas: number;
  otsAfectadas: number;
  lineasElegibles: number;
  lineasNoElegibles: readonly { readonly estado: string }[];
  etiquetaAccion: string;
}): ReactNode {
  const pluralLinea = lineasElegibles === 1 ? '' : 's';
  return (
    <div className="modal-resumen-seleccion">
      <p className="pequena suave">
        {otsAfectadas === otsSeleccionadas ? (
          <>
            {otsSeleccionadas} OT · {lineasElegibles} línea{pluralLinea} {etiquetaAccion}
          </>
        ) : (
          <>
            {otsSeleccionadas} OT seleccionada{otsSeleccionadas === 1 ? '' : 's'}. De ellas,{' '}
            <strong>{otsAfectadas}</strong> tiene{otsAfectadas === 1 ? '' : 'n'} líneas {etiquetaAccion}:{' '}
            {lineasElegibles} línea{pluralLinea}
          </>
        )}
        {lineasNoElegibles.length > 0 && (
          <>
            {' '}
            · <strong>{lineasNoElegibles.length}</strong> línea{lineasNoElegibles.length === 1 ? '' : 's'} no elegible
            {lineasNoElegibles.length === 1 ? '' : 's'}
          </>
        )}
        .
      </p>
      {lineasNoElegibles.length > 0 && (
        <p className="pequena tenue">No elegibles: {resumenLineasPorEstado(lineasNoElegibles)}.</p>
      )}
    </div>
  );
}

/** Estado de una no conformidad (FASE 4.2-B/4.3-C). Mapa central (UX-1C). */
export function EstadoNc({ estado }: { estado: string }): ReactNode {
  return <Estado d={estadoNc(estado)} />;
}

/** Sólo la etiqueta, para sitios que no admiten un `ReactNode` (p.ej. texto de historial). */
export function etiquetaEstadoNc(estado: string): string {
  return estadoNc(estado).label;
}

const ORIGENES_OT: Readonly<Record<string, DescripcionEstado>> = {
  PLAN: { etiqueta: 'Plan', variante: 'neutra' },
  PUNTUAL: { etiqueta: 'Manual / Extraordinaria', variante: 'info' },
  EXTRA: { etiqueta: 'Trabajo extra', variante: 'info' },
  CORRECTIVA: { etiqueta: 'Correctiva', variante: 'aviso' },
};

/** Origen de una OT (FASE 4.3-F, FUNC-OT-01). Mismo criterio que los estados. */
export function EstadoOrigen({ origen }: { origen: string }): ReactNode {
  const d = descripcionDe(ORIGENES_OT, origen);
  return <span className={`etiqueta ${d.variante}`}>{d.etiqueta}</span>;
}

/** Sólo la etiqueta, para sitios que no admiten un `ReactNode` (p.ej. `<option>`). */
export function etiquetaOrigen(origen: string): string {
  return descripcionDe(ORIGENES_OT, origen).etiqueta;
}

/** Estado de la versión de un plan de limpieza (BORRADOR/VIGENTE/SUSTITUIDO). */
export function EstadoPlan({ estado }: { estado: string }): ReactNode {
  return <Estado d={estadoPlanLimpieza(estado)} />;
}

export function EstadoImportacion({ estado }: { estado: string }): ReactNode {
  const clase =
    estado === 'CONFIRMADA'
      ? 'exito'
      : estado === 'FALLIDA'
        ? 'error'
        : estado === 'DESCARTADA'
          ? 'neutra'
          : 'info';
  return <span className={`etiqueta ${clase}`}>{estado}</span>;
}

/** Fecha/hora local en formato aceptado por `<input type="datetime-local">`, sin segundos. */
export function ahoraParaInput(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

/** Cifra destacada con su etiqueta — resultados de lote, cabeceras de resumen. */
export function Cifra({ n, t }: { n: number; t: string }): ReactNode {
  return (
    <div className="cifra">
      <div className="n">{n}</div>
      <div className="t">{t}</div>
    </div>
  );
}

/** Fecha ISO a formato local corto. Las fechas viajan como texto AAAA-MM-DD. */
/**
 *  Fecha LOCAL (AAAA-MM-DD) de `ahora` + `dias`. Las fechas de negocio (fecha de una OT, vigencia de un plan)
 *  son locales: `toISOString()` da la de UTC, que entre las 00:00 y las 02:00 de Madrid es la de AYER
 *  (UAT 9.6-B1-B: OT manual basada en la versión anterior del plan).
 */
export function hoyLocal(dias = 0, ahora: Date = new Date()): string {
  const d = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate() + dias);
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Día LOCAL (dd/mm/aaaa) de un INSTANTE ISO. `fecha()` corta el texto y daría el día UTC (de madrugada, el de ayer). */
export function fechaDeInstante(iso: string | null): string {
  if (iso === null || iso === '') return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? fecha(iso) : fecha(hoyLocal(0, d));
}

/**
 * F10.1: operarios por NOMBRE, nunca por su código interno — «Ana Ruiz, Luis Gil +2». El código sólo
 * aparece si no hay nombre (dato antiguo). `max` nombres visibles; el resto se resume en «+N».
 */
export function nombresOperarios(lista: readonly { nombre?: string | null; codigo: string }[], max = 2): string {
  const nombres = lista.map((o) => (o.nombre ?? '').trim() || o.codigo);
  if (nombres.length <= max) return nombres.join(', ');
  return `${nombres.slice(0, max).join(', ')} +${String(nombres.length - max)}`;
}

/**
 * F10.4 — «07:00–08:30», y «18:06–04:06 (+1 día)» cuando el fin cae otro día. Sólo presentación: el
 * inicio es la hora LOCAL en minutos desde medianoche (0–1439) y la duración, la planificada; nunca se
 * recalcula nada. Compartido por Planner, Hoy, vista rápida y Mis trabajos.
 */
export function rangoHorario(inicioMin: number, duracionMin: number): string {
  const hhmm = (m: number): string => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  const finAbsoluto = inicioMin + Math.max(0, duracionMin);
  const dias = Math.floor(finAbsoluto / (24 * 60));
  const fin = finAbsoluto % (24 * 60);
  const sufijo = dias === 0 ? '' : dias === 1 ? ' (+1 día)' : ` (+${String(dias)} días)`;
  return `${hhmm(inicioMin)}–${hhmm(fin)}${sufijo}`;
}

export function fecha(iso: string | null): string {
  if (iso === null || iso === '') return '—';
  const [a, m, d] = iso.slice(0, 10).split('-');
  return a === undefined || m === undefined || d === undefined ? iso : `${d}/${m}/${a}`;
}

export function fechaHora(iso: string | null): string {
  if (iso === null || iso === '') return '—';
  //  F11.1: el backend formatea muchos instantes con `to_char(..., 'MSOF')`, que da el desfase
  //  como «+02» — y `Date` sólo entiende «+02:00». Sin esto se pintaba el ISO crudo.
  const f = new Date(iso.replace(/([+-]\d{2})$/, '$1:00'));
  return Number.isNaN(f.getTime()) ? iso : f.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * Siguiente estado de orden al pulsar una cabecera: la misma columna
 * alterna ASC/DESC; una columna distinta siempre empieza en ASC. Pura y
 * sin conocimiento de qué lista la usa — cada página decide sus propias
 * claves de columna.
 */
export function alternarOrden(
  columna: string,
  ordenActual: string,
  dirActual: 'asc' | 'desc',
): { orden: string; dir: 'asc' | 'desc' } {
  if (columna === ordenActual) return { orden: columna, dir: dirActual === 'asc' ? 'desc' : 'asc' };
  return { orden: columna, dir: 'asc' };
}

/**
 * Cabecera de tabla ordenable: pinta la flecha sólo en la columna activa y
 * expone `aria-sort` en el `<th>` para quien use un lector de pantalla. No
 * decide el siguiente estado — eso es `alternarOrden()` — sólo avisa del
 * clic con `onClick`.
 */
export function CabeceraOrdenable({
  texto,
  columna,
  ordenActual,
  dirActual,
  onClick,
  className,
}: {
  texto: string;
  columna: string;
  ordenActual: string;
  dirActual: 'asc' | 'desc';
  onClick: () => void;
  className?: string;
}): ReactNode {
  const activa = columna === ordenActual;
  return (
    <th className={className} aria-sort={activa ? (dirActual === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="th-ordenable" onClick={onClick}>
        {texto}
        {activa && (
          <span aria-hidden="true" className="th-ordenable-flecha">
            {dirActual === 'asc' ? ' ▲' : ' ▼'}
          </span>
        )}
      </button>
    </th>
  );
}

export function CampoTexto({
  label,
  value,
  onChange,
  error,
  disabled,
  required,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  type?: 'text' | 'email';
}): ReactNode {
  const id = useId();
  return (
    <div className="campo">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error !== undefined ? 'true' : undefined}
        disabled={disabled}
        required={required}
      />
      {error !== undefined && <div className="error-campo">{error}</div>}
    </div>
  );
}

const ETIQUETAS_DIA = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

export function DiasOperativosInput({
  value,
  onChange,
  disabled,
}: {
  value: number[] | null;
  onChange: (v: number[] | null) => void;
  disabled?: boolean;
}): ReactNode {
  const todos = value === null;
  const sel = new Set(value ?? []);

  function toggleTodos() {
    onChange(todos ? [1, 2, 3, 4, 5] : null);
  }

  function toggleDia(num: number) {
    if (todos) {
      onChange([1, 2, 3, 4, 5, 6, 7].filter((d) => d !== num));
      return;
    }
    const nuevo = sel.has(num)
      ? [...sel].filter((d) => d !== num).sort((a, b) => a - b)
      : [...sel, num].sort((a, b) => a - b);
    onChange(nuevo.length === 7 ? null : nuevo);
  }

  return (
    <div className="dias-toggle">
      <label>
        <input type="checkbox" checked={todos} onChange={toggleTodos} disabled={disabled} />
        Todos
      </label>
      {ETIQUETAS_DIA.map((nombre, i) => {
        const num = i + 1;
        return (
          <label key={num}>
            <input
              type="checkbox"
              checked={todos || sel.has(num)}
              onChange={() => toggleDia(num)}
              disabled={disabled}
            />
            {nombre}
          </label>
        );
      })}
    </div>
  );
}

/**
 * FILTROS RESPONSIVE — FASE 3.3-D2/D3
 *
 * Envuelve el `.filtros` de siempre para que, en móvil, la búsqueda quede
 * siempre visible y el resto de controles vivan detrás de un botón
 * «Filtros (N)». No sabe qué es Operarios, un Cliente o un estado de
 * dominio: cada página sigue componiendo sus propios `.campo` (`children`)
 * y su propio input de búsqueda (`busqueda`); esto sólo decide botón,
 * contador, expandido/colapsado y accesibilidad. Tampoco toca
 * `useListaUrl` ni `URLSearchParams` — el contador es un número que cada
 * página calcula con sus propias claves de filtro (nunca `q`/`orden`/
 * `dir`/`pagina`) y pasa ya hecho.
 *
 * VISIBILIDAD DE ESCRITORIO GARANTIZADA POR CSS, NO POR REACT
 *
 * `children` se renderiza SIEMPRE, sin condicionar por `abierto`: en
 * escritorio/intermedio (>600px) `.filtros-colapsable` es `display:
 * contents` (sus hijos son campos sueltos de `.filtros`, igual que
 * siempre) y en móvil pasa a bloque colapsable, oculto con `display: none`
 * cuando `data-abierto="false"` — eso ya saca el contenido cerrado de la
 * navegación por teclado, sin necesidad de desmontarlo. Así un resize
 * nunca puede dejar escritorio sin filtros por culpa de un estado de React
 * pensado para móvil, y un resize a móvil aplica su colapso sin perder
 * ningún valor (nada se desmonta).
 */
export function FiltrosResponsive({
  contador = 0,
  busqueda,
  children,
}: {
  /** Filtros de refinamiento activos (no cuenta `q`, `orden`, `dir` ni `pagina`). */
  contador?: number;
  /** Campo de búsqueda de la página, si tiene: queda siempre visible, fuera del panel. */
  busqueda?: ReactNode;
  children: ReactNode;
}): ReactNode {
  const id = useId();
  const [abierto, setAbierto] = useState(false);

  //  Si el contador vuelve a cero (el usuario limpió, o quitó a mano cada
  //  filtro hasta no dejar ninguno activo), el panel se cierra solo: no
  //  tiene sentido seguir ocupando espacio por un panel vacío. No hace
  //  falta que cada página sepa esto ni que llame a nada: se deriva del
  //  mismo `contador` que ya recibe para el badge.
  useEffect(() => {
    if (contador === 0) setAbierto(false);
  }, [contador]);

  return (
    <div className="filtros">
      {busqueda}
      <button
        type="button"
        className="boton-filtros"
        aria-expanded={abierto}
        aria-controls={id}
        onClick={() => setAbierto((v) => !v)}
      >
        Filtros{contador > 0 ? ` (${contador})` : ''}
      </button>
      <div id={id} className="filtros-colapsable" data-abierto={abierto}>
        {children}
      </div>
    </div>
  );
}

/**
 * BARRA DE ACCIONES (FASE 9.1-C; UX-1B: jerarquía)
 *
 * Hallazgo de UAT móvil: con varias acciones, la cabecera de una ficha
 * (Activo, Incidencia de mantenimiento…) se salía del viewport. Este
 * contenedor (más `.cabecera-acciones` en CSS) envuelve en escritorio y, en
 * móvil, apila los botones en filas de 44 px a todo el ancho — nunca scroll
 * horizontal ni `nowrap`.
 *
 * UX-1B — una acción PRINCIPAL visible, como mucho una SECUNDARIA y el resto
 * en «Más acciones» (las destructivas, siempre ahí y nunca en sólido). La
 * página decide qué acciones existen (permisos, estado): este componente sólo
 * las coloca. Con `children` se comporta como antes (compatibilidad).
 */
export interface AccionMenu {
  readonly texto: string;
  readonly alPulsar?: () => void;
  /** Navegación en vez de acción. */
  readonly a?: string;
  /** Destructiva (anular, dar de baja, eliminar…): texto en rojo; la confirmación final va en un diálogo. */
  readonly peligro?: boolean;
  readonly deshabilitada?: boolean;
}

export function BarraAcciones({ etiqueta = 'Acciones', principal, secundaria, mas, children, className = '' }: {
  etiqueta?: string;
  principal?: ReactNode;
  secundaria?: ReactNode;
  /** Acciones de menor uso; los `false`/`null` (sin permiso, no aplica) se descartan. */
  mas?: readonly (AccionMenu | false | null | undefined)[];
  children?: ReactNode;
  className?: string;
}): ReactNode {
  const menu = (mas ?? []).filter((x): x is AccionMenu => Boolean(x));
  return (
    <div className={`barra-acciones ${className}`.trim()} role="group" aria-label={etiqueta}>
      {principal}
      {secundaria}
      {children}
      {menu.length > 0 && <MasAcciones acciones={menu} />}
    </div>
  );
}

/**
 * UX-1B — «Más acciones»: botón con menú accesible, sin librería. aria-haspopup/aria-expanded;
 * al abrir, el foco va a la primera opción; flechas, Inicio/Fin; Escape o Tab cierran (Escape devuelve
 * el foco al botón); un clic fuera también cierra.
 */
export function MasAcciones({ acciones, texto = 'Más acciones' }: { acciones: readonly AccionMenu[]; texto?: string }): ReactNode {
  const [abierto, setAbierto] = useState(false);
  const id = useId();
  const raiz = useRef<HTMLDivElement>(null);
  const boton = useRef<HTMLButtonElement>(null);
  const opciones = () => [...(raiz.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])') ?? [])];

  useEffect(() => {
    if (!abierto) return;
    opciones()[0]?.focus();
    function fuera(e: MouseEvent): void {
      if (raiz.current !== null && !raiz.current.contains(e.target as Node)) setAbierto(false);
    }
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  function teclado(e: EventoTeclado<HTMLDivElement>): void {
    if (!abierto) return;
    const ops = opciones();
    const i = ops.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setAbierto(false); boton.current?.focus(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); ops[(i + 1) % ops.length]?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); ops[(i - 1 + ops.length) % ops.length]?.focus(); }
    else if (e.key === 'Home') { e.preventDefault(); ops[0]?.focus(); }
    else if (e.key === 'End') { e.preventDefault(); ops[ops.length - 1]?.focus(); }
    else if (e.key === 'Tab') setAbierto(false);
  }
  function elegir(a: AccionMenu): void {
    if (a.deshabilitada === true) return;
    setAbierto(false);
    a.alPulsar?.();
  }

  return (
    <div className="mas-acciones" ref={raiz} onKeyDown={teclado}>
      <button type="button" ref={boton} className="mas-acciones-boton" aria-haspopup="menu" aria-expanded={abierto} aria-controls={id}
        onClick={() => setAbierto((v) => !v)}>
        {texto} <span aria-hidden="true">▾</span>
      </button>
      {abierto && (
        <div className="mas-acciones-menu" role="menu" id={id} aria-label={texto}>
          {acciones.map((a) => a.a !== undefined ? (
            <Link key={a.texto} role="menuitem" tabIndex={-1} to={a.a} className={a.peligro === true ? 'peligro' : undefined}
              onClick={() => setAbierto(false)}>{a.texto}</Link>
          ) : (
            <button key={a.texto} type="button" role="menuitem" tabIndex={-1} className={a.peligro === true ? 'peligro' : undefined}
              aria-disabled={a.deshabilitada === true ? true : undefined} onClick={() => elegir(a)}>{a.texto}</button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * UX-1B — confirmación de una acción irreversible o destructiva, sobre el Modal de siempre (foco atrapado,
 * Escape). Sustituye a `window.confirm`. El botón final es sólido (rojo si `variante="peligro"`): es el
 * único sitio donde una acción destructiva aparece en sólido. No es para formularios: sólo confirmar.
 */
export function ConfirmDialog({ titulo, descripcion, consecuencia, confirmar, variante = 'peligro', alConfirmar, cerrar, ocupado = false }: {
  titulo: string;
  descripcion?: ReactNode;
  consecuencia?: ReactNode;
  confirmar: string;
  variante?: 'peligro' | 'principal';
  alConfirmar: () => void | Promise<void>;
  cerrar: () => void;
  ocupado?: boolean;
}): ReactNode {
  return (
    <Modal titulo={titulo} cerrar={cerrar} pie={(
      <div className="pie-confirmacion">
        <button type="button" onClick={cerrar} disabled={ocupado}>Cancelar</button>
        <button type="button" className={variante} disabled={ocupado} onClick={() => void alConfirmar()}>{ocupado ? 'Un momento…' : confirmar}</button>
      </div>
    )}>
      {descripcion !== undefined && <div className="confirmacion-descripcion">{descripcion}</div>}
      {consecuencia !== undefined && <p className="nota aviso confirmacion-consecuencia">{consecuencia}</p>}
    </Modal>
  );
}
